import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Link } from 'react-router-dom';
import axios from 'axios';
import HelmetSEO from '../../componentesEspeciales/helmetSEO';

const pasos = [
    {
        numero: 1,
        titulo: 'Tipo de servicio',
        descripcion: 'Define si tu servicio es presencial o digital, y si vas a domicilio, atiendes en tu local o ambas cosas.',
        icono: 'bi-toggles',
    },
    {
        numero: 2,
        titulo: 'Título, descripción y ubicación',
        descripcion: 'Ponle nombre y descripción a lo que ofreces, elige la categoría y dinos en qué ciudad o zona trabajas.',
        icono: 'bi-pencil-square',
    },
    {
        numero: 3,
        titulo: 'Fotos de tu trabajo',
        descripcion: 'Sube hasta 8 fotos que muestren lo que haces. Las mejores fotos generan más confianza y más reservas.',
        icono: 'bi-images',
    },
    {
        numero: 4,
        titulo: 'Paquetes y precios',
        descripcion: 'Crea hasta 3 paquetes (Básico, Premium, Avanzado) con su precio, duración y lo que incluye cada uno.',
        icono: 'bi-tags',
    },
    {
        numero: 5,
        titulo: 'Preguntas frecuentes',
        descripcion: 'Agrega las preguntas que más te hacen tus clientes y respóndelas. Así el cliente llega más seguro a reservar.',
        icono: 'bi-patch-question',
    },
];

function ComoFunciona() {
    useOutletContext();
    const [categorias, setCategorias] = useState([]);

    useEffect(() => {
        axios.post("/obtener-categorias-populares", { params: { cantidad: 16 } })
            .then(res => setCategorias(res.data?.categoriasPopulares || []))
            .catch(() => {});
    }, []);

    return (
        <>
            <HelmetSEO
                titulo="Cómo funciona Zervicy | Ofrece tus servicios"
                descripcion="Descubre cómo unirte a Zervicy para ofrecer tus servicios, crear tu perfil y recibir reservas fácilmente desde cualquier parte de Colombia."
                imagen="https://zervicy.s3.us-east-1.amazonaws.com/imagenes/marca/comofunciona.png"
            />

            {/* ── HERO ─────────────────────────────────────────────── */}
            <section className="position-relative text-white py-5" style={{ overflow: 'hidden' }}>
                {/* Fondo: imagen */}
                <div className="position-absolute top-0 start-0 w-100 h-100" style={{ zIndex: 0 }}>
                    <img
                        src="https://zervicy.s3.us-east-1.amazonaws.com/imagenes/marca/comofunciona.png"
                        alt=""
                        className="w-100 h-100"
                        style={{ objectFit: 'cover', objectPosition: 'center' }}
                    />
                </div>

                {/* Overlay azul oscuro (mismo estilo que el hero de Home.jsx) para que el texto resalte */}
                <div
                    className="position-absolute top-0 start-0 w-100 h-100"
                    style={{
                        background: 'linear-gradient(135deg, rgba(var(--azul-muy-oscuro-rgb), 0.88) 0%, rgba(var(--azul-gradiente-2-rgb), 0.72) 55%, rgba(var(--azul-gradiente-3-rgb), 0.62) 100%)',
                        zIndex: 1,
                    }}
                />

                <div className="container py-3 position-relative" style={{ zIndex: 2 }}>
                    <div className="row justify-content-center text-center">
                        <div className="col-12 col-lg-8">
                            <span className="badge rounded-pill px-3 py-2 mb-3" style={{ backgroundColor: 'var(--naranja-cta)' }}>
                                Marketplace de servicios
                            </span>
                            <h1 className="display-5 fw-bold mb-3">¿Qué es Zervicy?</h1>
                            <p className="lead opacity-75 mb-4">
                                Zervicy es el lugar donde puedes <strong>ofrecer o contratar servicios de todo tipo</strong> — desde aseo, mascotas y belleza hasta diseño digital, clases y mucho más. Conectamos personas con profesionales en Colombia.
                            </p>
                        </div>
                    </div>

                    {/* CATEGORÍAS */}
                    <div className="row justify-content-center g-2 mt-2">
                        {categorias.map((cat) => (
                            <div key={cat.categoria} className="col-auto">
                                <span className="badge rounded-pill px-3 py-2 d-flex align-items-center gap-2"
                                    style={{ backgroundColor: 'rgba(var(--blanco-rgb), 0.12)', border: '1px solid rgba(var(--blanco-rgb), 0.2)', fontSize: '13px' }}>
                                    <img src={cat.icono} alt={cat.categoria} style={{ width: 16, height: 16, filter: 'brightness(0) invert(1)' }} />
                                    {cat.categoria}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── ¿QUÉ QUIERES HACER? ───────────────────────────────── */}
            <section className="py-5 bg-white">
                <div className="container">
                    <div className="text-center mb-4">
                        <h2 className="h3 fw-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>¿Qué quieres hacer?</h2>
                        <p className="text-muted">Elige tu camino dentro de Zervicy.</p>
                    </div>
                    <div className="row g-4 justify-content-center">
                        {/* COMPRADOR */}
                        <div className="col-12 col-md-5">
                            <div className="card border-0 shadow-sm rounded-4 h-100 p-4 text-center">
                                <div className="mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center"
                                    style={{ width: 64, height: 64, backgroundColor: 'rgba(var(--azul-logo-claro-rgb), 0.1)', color: 'var(--azul-logo-claro)' }}>
                                    <i className="bi bi-search fs-3"></i>
                                </div>
                                <h4 className="fw-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>Quiero contratar</h4>
                                <p className="text-muted texto-regular mb-4">
                                    Busca entre cientos de profesionales verificados. Compara, reserva y paga de forma segura desde la web o la app.
                                </p>
                                <Link to="/busqueda" className="btn rounded-pill px-4 mt-auto"
                                    style={{ backgroundColor: 'var(--azul-logo-claro)', color: 'white', border: 'none' }}>
                                    <i className="bi bi-search me-2"></i>
                                    Buscar servicios
                                </Link>
                            </div>
                        </div>

                        {/* VENDEDOR */}
                        <div className="col-12 col-md-5">
                            <div className="card border-0 shadow-sm rounded-4 h-100 p-4 text-center"
                                style={{ border: '2px solid var(--naranja-cta) !important' }}>
                                <div className="mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center"
                                    style={{ width: 64, height: 64, backgroundColor: 'rgba(var(--naranja-cta-rgb), 0.1)', color: 'var(--naranja-cta)' }}>
                                    <i className="bi bi-briefcase fs-3"></i>
                                </div>
                                <h4 className="fw-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>Quiero ofrecer mis servicios</h4>
                                <p className="text-muted texto-regular mb-4">
                                    Publica lo que sabes hacer, define tus precios y empieza a recibir reservas y pagos directamente en Zervicy.
                                </p>
                                <a href="#como-vendedor" className="btn rounded-pill px-4 mt-auto"
                                    style={{ backgroundColor: 'var(--naranja-cta)', color: 'white', border: 'none' }}>
                                    <i className="bi bi-arrow-down-circle me-2"></i>
                                    Ver cómo funciona
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── INTRO VENDEDOR + VIDEO ─────────────────────────────── */}
            <section id="como-vendedor" className="py-5" style={{ backgroundColor: 'var(--color-bg-surface-alt)', scrollMarginTop: '70px' }}>
                <div className="container">
                    <div className="row justify-content-center text-center">
                        <div className="col-12 col-lg-8">
                            <span className="badge rounded-pill px-3 py-2 mb-3" style={{ backgroundColor: 'var(--azul-logo-claro)', color: 'white' }}>
                                Para vendedores
                            </span>
                            <h2 className="display-6 fw-bold mb-3" style={{ color: 'var(--color-text-primary)' }}>
                                ¿Cómo ofrecer tus servicios en Zervicy?
                            </h2>
                            <p className="text-muted mb-4" style={{ fontSize: '18px' }}>
                                Solo necesitas crear una cuenta gratuita. En minutos puedes tener tu servicio publicado y empezar a recibir reservas.
                            </p>
                            <div className="ratio ratio-16x9 rounded-4 overflow-hidden shadow mb-4">
                                <iframe
                                    src="https://www.youtube.com/embed/veBkPx1yUkc"
                                    title="Bienvenido a Zervicy"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                            <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                                <Link to="/registrar" className="btn btn-lg rounded-pill px-4" style={{ backgroundColor: 'var(--naranja-cta)', color: 'white', border: 'none' }}>
                                    <i className="bi bi-person-plus me-2"></i>
                                    Crear cuenta gratis
                                </Link>
                                <Link to="/iniciar-sesion" className="btn btn-lg rounded-pill px-4 btn-outline-secondary">
                                    <i className="bi bi-box-arrow-in-right me-2"></i>
                                    Ya tengo cuenta
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── REQUISITOS PARA VENDER ────────────────────────────── */}
            <section className="py-5 bg-white">
                <div className="container">
                    <div className="text-center mb-5">
                        <span className="badge rounded-pill px-3 py-2 mb-3" style={{ backgroundColor: 'rgba(var(--naranja-cta-rgb), 0.12)', color: 'var(--naranja-cta)', fontSize: '13px' }}>
                            Antes de publicar
                        </span>
                        <h2 className="h3 fw-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>¿Qué necesitas para empezar a vender?</h2>
                        <p className="text-muted">Zervicy garantiza pagos seguros y vendedores verificados. Por eso te pedimos dos cosas.</p>
                    </div>

                    <div className="row g-4 justify-content-center">
                        {/* VERIFICACIÓN DE IDENTIDAD */}
                        <div className="col-12 col-md-5">
                            <div className="card border-0 shadow-sm rounded-4 h-100 p-4">
                                <div className="d-flex align-items-center gap-3 mb-3">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{ width: 56, height: 56, backgroundColor: 'rgba(var(--azul-logo-claro-rgb), 0.1)', color: 'var(--azul-logo-claro)' }}>
                                        <i className="bi bi-person-badge fs-4"></i>
                                    </div>
                                    <div>
                                        <h5 className="fw-bold mb-0" style={{ color: 'var(--color-text-primary)' }}>Verificación de identidad</h5>
                                        <span className="badge rounded-pill" style={{ backgroundColor: 'rgba(var(--azul-logo-claro-rgb), 0.1)', color: 'var(--azul-logo-claro)', fontSize: '11px' }}>Obligatorio</span>
                                    </div>
                                </div>
                                <p className="text-muted texto-regular mb-3">
                                    Para proteger a los compradores y garantizar que eres quien dices ser, te pediremos una foto de tu cédula y una selfie para verificar tu identidad antes de publicar tu primer servicio.
                                </p>
                                <ul className="list-unstyled mb-0">
                                    <li className="d-flex align-items-center gap-2 mb-2 text-muted small">
                                        <i className="bi bi-check-circle-fill" style={{ color: 'var(--azul-logo-claro)' }}></i>
                                        Foto de tu cédula (ambas caras)
                                    </li>
                                    <li className="d-flex align-items-center gap-2 mb-2 text-muted small">
                                        <i className="bi bi-check-circle-fill" style={{ color: 'var(--azul-logo-claro)' }}></i>
                                        Selfie de verificación (Face ID)
                                    </li>
                                    <li className="d-flex align-items-center gap-2 text-muted small">
                                        <i className="bi bi-check-circle-fill" style={{ color: 'var(--azul-logo-claro)' }}></i>
                                        Revisión en menos de 24 horas
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* INFORMACIÓN BANCARIA */}
                        <div className="col-12 col-md-5">
                            <div className="card border-0 shadow-sm rounded-4 h-100 p-4">
                                <div className="d-flex align-items-center gap-3 mb-3">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{ width: 56, height: 56, backgroundColor: 'rgba(var(--naranja-cta-rgb), 0.1)', color: 'var(--naranja-cta)' }}>
                                        <i className="bi bi-bank fs-4"></i>
                                    </div>
                                    <div>
                                        <h5 className="fw-bold mb-0" style={{ color: 'var(--color-text-primary)' }}>Datos bancarios</h5>
                                        <span className="badge rounded-pill" style={{ backgroundColor: 'rgba(var(--naranja-cta-rgb), 0.12)', color: 'var(--naranja-cta)', fontSize: '11px' }}>Para recibir pagos</span>
                                    </div>
                                </div>
                                <p className="text-muted texto-regular mb-3">
                                    Cuando completes una reserva, Zervicy te transfiere el dinero directamente a tu cuenta. Solo necesitas registrar tu información bancaria una vez.
                                </p>
                                <ul className="list-unstyled mb-0">
                                    <li className="d-flex align-items-center gap-2 mb-2 text-muted small">
                                        <i className="bi bi-check-circle-fill" style={{ color: 'var(--naranja-cta)' }}></i>
                                        Cuenta bancaria, Nequi o Daviplata
                                    </li>
                                    <li className="d-flex align-items-center gap-2 mb-2 text-muted small">
                                        <i className="bi bi-check-circle-fill" style={{ color: 'var(--naranja-cta)' }}></i>
                                        Transferencia automática al completar
                                    </li>
                                    <li className="d-flex align-items-center gap-2 text-muted small">
                                        <i className="bi bi-check-circle-fill" style={{ color: 'var(--naranja-cta)' }}></i>
                                        Sin cobros ocultos ni demoras
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── 5 PASOS ───────────────────────────────────────────── */}
            <section className="py-5 bg-white">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="h3 fw-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>Crea tu servicio en 5 pasos</h2>
                        <p className="text-muted">Sencillo, rápido y sin complicaciones.</p>
                    </div>

                    <div className="row g-4 justify-content-center">
                        {pasos.map((paso) => (
                            <div key={paso.numero} className="col-12 col-sm-6 col-lg-4">
                                <div className="card border-0 shadow-sm rounded-4 h-100 p-4">
                                    <div className="d-flex align-items-center gap-3 mb-3">
                                        <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{ width: 48, height: 48, backgroundColor: 'var(--azul-logo-claro)', color: 'white' }}>
                                            <i className={`bi ${paso.icono} fs-5`}></i>
                                        </div>
                                        <span className="fw-bold text-muted" style={{ fontSize: '13px' }}>PASO {paso.numero}</span>
                                    </div>
                                    <h5 className="fw-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>{paso.titulo}</h5>
                                    <p className="text-muted mb-0 texto-regular">{paso.descripcion}</p>
                                </div>
                            </div>
                        ))}

                        {/* CARD EXTRA - ya está listo */}
                        <div className="col-12 col-sm-6 col-lg-4">
                            <div className="card border-0 rounded-4 h-100 p-4 text-white"
                                style={{ background: 'linear-gradient(135deg, var(--naranja-cta), var(--naranja-oscuro))' }}>
                                <div className="d-flex align-items-center gap-3 mb-3">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{ width: 48, height: 48, backgroundColor: 'rgba(var(--blanco-rgb), 0.2)' }}>
                                        <i className="bi bi-rocket-takeoff fs-5"></i>
                                    </div>
                                    <span className="fw-bold opacity-75" style={{ fontSize: '13px' }}>Y LISTO</span>
                                </div>
                                <h5 className="fw-bold mb-2">Tu servicio está publicado</h5>
                                <p className="mb-0 opacity-85 texto-regular">Empieza a recibir reservas y pagos directamente desde Zervicy. Tú solo te preocupas por prestar el servicio.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── VIDEO CÓMO CREAR SERVICIO ─────────────────────────── */}
            <section className="py-5" style={{ backgroundColor: 'var(--color-bg-surface-alt)' }}>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-8 text-center">
                            <h2 className="h3 fw-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>Te lo explicamos paso a paso</h2>
                            <p className="text-muted mb-4">Este video te guía por todo el proceso de creación de tu servicio en Zervicy.</p>
                            <div className="ratio ratio-16x9 rounded-4 overflow-hidden shadow">
                                <iframe
                                    src="https://www.youtube.com/embed/nDnfyARN4Xw"
                                    title="Cómo crear tu servicio en Zervicy"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── DESCARGA LA APP ───────────────────────────────────── */}
            <section className="py-5 bg-white">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-8 text-center">
                            <h2 className="h3 fw-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>También desde tu celular</h2>
                            <p className="text-muted mb-4">Gestiona tus reservas, mensajes y pagos desde la app de Zervicy. Disponible para iOS y Android.</p>
                            <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                                <a href="https://apps.apple.com/co/app/zervicy/id6761780592"
                                    target="_blank" rel="noreferrer"
                                    className="btn btn-lg rounded-pill px-4"
                                    style={{ backgroundColor: 'var(--azul-logo-fondo)', color: 'white', border: 'none' }}>
                                    <i className="bi bi-apple me-2"></i>
                                    Descargar para iPhone
                                </a>
                                <a href="https://play.google.com/store/apps/details?id=com.zervicy.app"
                                    target="_blank" rel="noreferrer"
                                    className="btn btn-lg rounded-pill px-4"
                                    style={{ backgroundColor: 'var(--azul-logo-fondo)', color: 'white', border: 'none' }}>
                                    <i className="bi bi-google-play me-2"></i>
                                    Descargar para Android
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CTA FINAL ─────────────────────────────────────────── */}
            <section className="py-5" style={{ background: 'linear-gradient(135deg, var(--azul-logo-fondo), var(--azul-medio))', color: 'white' }}>
                <div className="container text-center">
                    <h2 className="h3 fw-bold mb-3">¿Listo para empezar?</h2>
                    <p className="opacity-75 mb-4">Crea tu cuenta gratis y publica tu primer servicio hoy.</p>
                    <Link to="/registrar"
                        className="btn btn-lg rounded-pill px-5"
                        style={{ backgroundColor: 'var(--naranja-cta)', color: 'white', border: 'none' }}>
                        <i className="bi bi-arrow-right-circle me-2"></i>
                        Crear cuenta gratis
                    </Link>
                </div>
            </section>
        </>
    );
}

export default ComoFunciona;
